import { defineStore } from 'pinia'
import i18n, { getGlobalLocale, setGlobalLocale } from '../utils/i18n'
import jQuery from '../utils/jquery'
import { Utils } from '../utils/utils'
import { syncMeetingSiteTheme } from '../utils/runtimeThemeSheet'

export const useMainStore = defineStore('main', {
  state: () => ({
    access_token: null,
    participant_access_token: null,
    site_access_token: null,
    user: {},
    enable_ui_navigation: true,
    meeting: {},
    identification_started: false,
    identification_triggered: false,
    meeting_started: false,
    meeting_disconnected_as: null,
    meeting_disconnected: false,
    meeting_ended_as: null,
    meeting_interrupted: false,
    identification_ended: false,
    meeting_ended: false,
    meeting_remote_participants: {},
    last_meeting_info: {},
    is_meeting_host: false,
    feedback_forms: [],
    forced_logout: false,
    product: false,
    status: null,
    notification_bar: {},
    notification_bar_closed_ids: [],
    devices: {},
    cam_settings: {},
    alwaysAvailableInterval: null,
    code_verifier: null,
    participant_meeting_state: null,
    identification_id: null,
    meeting_id: null,
    current_url: null,
    signicat_redirect_uri: null,
    zynyoEmailAddress: null,
    signicatConfig: {},
    isVideoMuted: false,
    isAudioMuted: false,
    productMode: 'WSD',
    isAndroidWebView: false,
    plannenSite: null,
    plannenSelectedAgent: null,
    plannenAvailableDate: [],
    meeting_link_session: false
  }),
  actions: {
    setZynyoEmailAddress(zynyoEmailAddress) {
      this.zynyoEmailAddress = zynyoEmailAddress
    },
    setCurrentUrl(currentUrl) {
      this.current_url = currentUrl
    },
    setMeetingId(meetingId) {
      this.meeting_id = meetingId
    },
    setCodeVerifier(codeVerifier) {
      this.code_verifier = codeVerifier
    },
    setSignicatRedirectUri(redirectUri) {
      this.signicat_redirect_uri = redirectUri
    },
    setParticipantMeetingState(participantMeetingState) {
      this.participant_meeting_state = participantMeetingState
    },
    setIdentificationId(identificationId) {
      this.identification_id = identificationId
    },
    setAccessToken(accessToken) {
      this.access_token = accessToken
    },
    setParticipantAccessToken(accessToken) {
      this.participant_access_token = accessToken
    },
    setSiteAccessToken(accessToken) {
      this.site_access_token = accessToken
    },
    setUser(user) {
      this.user = user
    },
    enableUINavigation(enable) {
      this.enable_ui_navigation = enable
    },
    setMeetingLinkSession(isMeetingLinkSession) {
      this.meeting_link_session = !!isMeetingLinkSession
    },
    setMeeting(meeting) {
      this.meeting = meeting ?? {}

      if (Object.keys(this.meeting).length > 0) {
        if (typeof this.meeting.site !== 'undefined') {
          syncMeetingSiteTheme(this.meeting.site)
        }
        if (typeof this.meeting.site !== 'undefined' && typeof this.meeting.site.feedback_agent !== 'undefined') {
          if (this.feedback_forms.filter(form => form.id === this.meeting.site.feedback_agent.id).length === 0) {
            this.feedback_forms.push(this.meeting.site.feedback_agent)
          }
        }
        if (typeof this.meeting.site !== 'undefined' && typeof this.meeting.site.feedback_participant !== 'undefined') {
          if (this.feedback_forms.filter(form => form.id === this.meeting.site.feedback_participant.id).length === 0) {
            this.feedback_forms.push(this.meeting.site.feedback_participant)
          }
        }
      } else {
        this.meeting_remote_participants = {}
        this.is_meeting_host = false
      }
    },
    setMeetingStarted(started) {
      this.meeting_started = started
    },
    setIdentificationStarted(started) {
      this.identification_started = started
    },
    setIdentificationTriggered(triggered) {
      this.identification_triggered = triggered
    },
    setMeetingDisconnectedAs(disconnectedAs) {
      this.meeting_disconnected_as = disconnectedAs
    },
    setMeetingDisconnected(disconnected) {
      this.meeting_disconnected = disconnected
    },
    setMeetingEndedAs(endedAs) {
      this.meeting_ended_as = endedAs
    },
    setMeetingInterrupted(interrupted) {
      this.meeting_interrupted = interrupted
    },
    setMeetingEnded(ended) {
      this.meeting_ended = ended
    },
    setIdentificationEnded(ended) {
      this.identification_ended = ended
    },
    setIsMeetingHost(isMeetingHost) {
      this.is_meeting_host = isMeetingHost
    },
    setForcedLogout(forced) {
      this.forced_logout = forced
    },
    setLocale() {
      const urlSearchParams = new URLSearchParams(window.location.search)
      const queryParams = Object.fromEntries(urlSearchParams.entries())
      const availableLocales = i18n.global.availableLocales
      if (queryParams.lang && availableLocales.includes(queryParams.lang)) {
        setGlobalLocale(queryParams.lang)
      } else if (this.getUser !== false && typeof this.getUser.locale !== 'undefined') {
        const userLang = this.getUser.locale
        const foundLocale = availableLocales.find(availableLocale => {
          return userLang.includes(availableLocale)
        })
        if (foundLocale) {
          setGlobalLocale(foundLocale)
        } else {
          const browserLang = navigator.language.split('-')[0]
          const foundBrowserLocale = availableLocales.find(availableLocale => {
            return browserLang.includes(availableLocale)
          })
          if (foundBrowserLocale) {
            setGlobalLocale(foundBrowserLocale)
          }
        }
      } else if (this.getMeeting !== false && typeof this.getMeeting.locale !== 'undefined') {
        const meetingLang = this.getMeeting.locale
        const foundLocale = availableLocales.find(availableLocale => {
          return meetingLang.includes(availableLocale)
        })
        if (foundLocale) {
          setGlobalLocale(foundLocale)
        } else {
          const browserLang = navigator.language.split('-')[0]
          const foundBrowserLocale = availableLocales.find(availableLocale => {
            return browserLang.includes(availableLocale)
          })
          if (foundBrowserLocale) {
            setGlobalLocale(foundBrowserLocale)
          }
        }
      } else {
        const browserLang = navigator.language.split('-')[0]
        const foundBrowserLocale = availableLocales.find(availableLocale => {
          return browserLang.includes(availableLocale)
        })
        if (foundBrowserLocale) {
          setGlobalLocale(foundBrowserLocale)
        }
      }

      jQuery('html').attr('lang', getGlobalLocale())
    },
    setProduct(product) {
      this.product = product
    },
    setStatus(status) {
      this.status = status
    },
    setNotificationBar(notification) {
      this.notification_bar = notification
    },
    addNotificationBarClosedId(id) {
      this.notification_bar_closed_ids.push(id)
    },
    addRemoteMeetingParticipant(event) {
      this.meeting_remote_participants[event.video.id] = event.real_name
    },
    removeRemoteMeetingParticipant(streamId) {
      delete this.meeting_remote_participants[streamId]
    },
    removeFeedbackForms() {
      this.feedback_forms = []
    },
    removeFeedbackFormById(id) {
      this.feedback_forms = this.feedback_forms.filter((form) => {
        return form.id !== id
      })
    },
    setLastMeetingInfo(info) {
      this.last_meeting_info = info
    },
    setDeviceList(devices) {
      this.devices = devices
    },
    setCamSettings(settings) {
      this.cam_settings = settings
    },
    setAlwaysAvailableInterval(intervalId) {
      this.alwaysAvailableInterval = intervalId
    },
    setSignicatConfig(signicatConfig) {
      this.signicatConfig = signicatConfig
    },
    setProductMode(mode) {
      this.productMode = mode
    },
    setIsAndroidWebView(isAndroidWebView) {
      this.isAndroidWebView = isAndroidWebView
    },
    setPlannenSite(site) {
      this.plannenSite = site
    },
    setPlannenSelectedAgent(agent) {
      this.plannenSelectedAgent = agent
    },
    setPlannenAvailableDate(availableDate) {
      this.plannenAvailableDate = availableDate
    }
  },
  getters: {
    getSignicatConfig (state) {
      return state.signicatConfig
    },
    getCurrentUrl (state) {
      return state.current_url
    },
    getMeetingId (state) {
      return state.meeting_id
    },
    getCodeVerifier: state => {
      return state.code_verifier
    },
    getSignicatRedirectUri: state => {
      return state.signicat_redirect_uri
    },
    getParticipantMeetingState: state => {
      return state.participant_meeting_state
    },
    getIdentificationId (state) {
      return state.identification_id
    },
    getAlwaysAvailableInterval: state => {
      return state.alwaysAvailableInterval
    },
    getAccessToken: state => {
      return state.access_token
    },
    getParticipantAccessToken: state => {
      return state.participant_access_token
    },
    getSiteAccessToken: state => {
      return state.site_access_token
    },
    getUserSites: state => {
      return state.user.sites
    },
    getSiteTree: state => {
      if (!Array.isArray(state.user?.sites)) {
        return []
      }
      let sitesCopy = JSON.parse(JSON.stringify(state.user.sites))
      let treeOrderedSites = sitesCopy.filter(site => !site.parent)
      for (const site of treeOrderedSites) {
        site.depth = 0
      }
      let sitesWithParent = sitesCopy.filter(site => site.parent)
      let attempts = 0
      do {
        // Just to be sure we don't infinite loop here
        attempts++
        for (const siteIndex in sitesWithParent) {
          let site = sitesWithParent[siteIndex]
          let shouldBreak = false
          for (let treeSiteIndex in treeOrderedSites) {
            let treeSite = treeOrderedSites[treeSiteIndex]
            if (treeSite.id === site.parent) {
              site.depth = treeSite.depth + 1
              treeOrderedSites.splice(parseInt(treeSiteIndex) + 1, 0, site)
              sitesWithParent.splice(siteIndex, 1)
              shouldBreak = true
              break
            }
          }
          if (shouldBreak) {
            break
          }
        }
      } while (sitesCopy.length > treeOrderedSites.length && attempts <= 100)

      return treeOrderedSites.sort((a, b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0))
    },
    getUser: state => {
      if (state.user !== null && Object.keys(state.user).length > 0) {
        return state.user
      } else {
        return false
      }
    },
    getUserAccount: state => {
      if (Object.keys(state.user).length > 0 && typeof state.user.sites !== 'undefined' && typeof state.user.sites[0] !== 'undefined') {
        return state.user.sites[0]
      } else {
        return false
      }
    },
    getUserAccountById: state => (id) => {
      if (Object.keys(state.user.sites).length > 0) {
        return state.user.sites.find(x => x.id === id)
      } else {
        return false
      }
    },
    isUINavigationEnabled: state => {
      return state.enable_ui_navigation
    },
    agentHasCallColleague: (state) => () => {
      return Object.keys(state.user).length > 0 && state.user.call_colleague === true
    },
    userHasFeature: (state) => (feature) => {
      return Object.keys(state.user).length > 0 && typeof state.user.entity !== 'undefined' && typeof state.user.entity.package !== 'undefined' && typeof state.user.entity.package.features !== 'undefined' && state.user.entity.package.features.includes(feature)
    },
    participantHasFeature: (state) => (feature) => {
      return Object.keys(state.meeting).length > 0 && typeof state.meeting.features !== 'undefined' && state.meeting.features[feature]
    },
    siteHasFeature: (state) => (feature) => {
      return Object.keys(state.meeting).length > 0 && typeof state.meeting.site !== 'undefined' && typeof state.meeting.site.enabled_features !== 'undefined' && state.meeting.site.enabled_features.includes(feature)
    },
    agentHasFeature: (state) => (feature) => {
      return Object.keys(state.user).length > 0 &&
        Array.isArray(state.user.sites) &&
        state.user.sites.some(site => Array.isArray(site?.enabled_features) && site.enabled_features.includes(feature))
    },
    /**
     * Profile (Mijn profiel) is only for agents on a planningstool site
     * (allow_scheduling_by_participant).
     */
    userCanAccessProfile: (state) => {
      const user = state.user
      if (!user || Object.keys(user).length === 0) {
        return false
      }
      const product = state.product === 'weseedo_direct' ? 'direct' : state.product
      if (product !== 'personal') {
        return false
      }
      if (!['agent', 'assistent_video'].includes(user.role)) {
        return false
      }
      return Array.isArray(user.sites) &&
        user.sites.some(site => site?.allow_scheduling_by_participant === true)
    },
    companyHasFeature: (state) => (feature) => {
      return state.meeting && Object.keys(state.meeting).length > 0 && typeof state.meeting.company !== 'undefined' && typeof state.meeting.company?.features !== 'undefined' && state.meeting.company?.features.includes(feature)
    },
    dealerHasFeature: (state) => (feature) => {
      return state.meeting && Object.keys(state.meeting).length > 0 && typeof state.meeting.company?.dealer !== 'undefined' && typeof state.meeting.company?.dealer?.features !== 'undefined' && state.meeting.company?.dealer?.features.includes(feature)
    },
    getMeeting: state => {
      if (state.meeting && Object.keys(state.meeting).length > 0) {
        return state.meeting
      } else {
        return false
      }
    },
    getMeetingStarted: state => {
      return state.meeting_started
    },
    getIdentificationStarted: state => {
      return state.identification_started
    },
    getIdentificationTriggered: state => {
      return state.identification_triggered
    },
    getMeetingDisconnectedAs: (state) => {
      if (state.meeting_disconnected_as === 'agent') {
        return 'agent'
      } else if (state.meeting_disconnected_as === 'participant') {
        return 'participant'
      } else {
        return null
      }
    },
    getMeetingDisconnected: (state) => {
      return state.meeting_disconnected
    },
    getMeetingEndedAs: (state) => {
      if (state.meeting_ended_as === 'agent') {
        return 'agent'
      } else if (state.meeting_ended_as === 'participant') {
        return 'participant'
      } else {
        return null
      }
    },
    getMeetingEnded: (state) => {
      return state.meeting_ended
    },
    getMeetingInterrupted: (state) => {
      return state.meeting_interrupted === true
    },
    // Only Direct/Meek agent mid-call reload recovery (interrupted end screen).
    // Do not treat a normal hangup's meeting_ended as recovery — that blocked
    // personal (and other) participants from rejoining via meeting token after reload.
    isRecoveringEndedMeeting: (state) => {
      return state.meeting_interrupted === true && state.meeting_ended_as === 'agent'
    },
    getIdentificationEnded: (state) => {
      return state.identification_ended
    },
    getIsMeetingHost: (state) => {
      return state.is_meeting_host
    },
    getForcedLogout: (state) => {
      return state.forced_logout
    },
    getProduct: (state) => {
      if (state.product === 'personal' || state.product === 'direct' || state.product === 'weseedo_direct') {
        return state.product === 'weseedo_direct' ? 'direct' : state.product
      } else {
        return null
      }
    },
    getStatus: (state) => {
      return state.status
    },
    getNotificationBar: (state) => {
      return state.notification_bar
    },
    getNotificationBarClosedIds: (state) => {
      return state.notification_bar_closed_ids
    },
    getRemoteMeetingParticipants: (state) => {
      if (Object.keys(state.meeting_remote_participants).length > 0) {
        return state.meeting_remote_participants
      } else {
        return false
      }
    },
    getFeedbackForms: (state) => {
      return state.feedback_forms
    },
    getLastMeetingInfo: (state) => {
      if (Object.keys(state.last_meeting_info).length > 0) {
        return state.last_meeting_info
      } else {
        return false
      }
    },
    getDeviceList: (state) => {
      return state.devices
    },
    getCamSettings: (state) => {
      if (Object.keys(state.cam_settings).length > 0) {
        return state.cam_settings
      } else {
        return false
      }
    },
    getIsMeetingAnnotationEnabled: (state) => {
      return state.user?.sites?.some(site => site.enable_meeting_annotation) || false
    },
    getProductMode: (state) => {
      return state.productMode
    },
    getIsAndroidWebView: (state) => {
      return state.isAndroidWebView
    },
    isMeekijken: (state) => {
      return state.productMode === 'Meek'
    },
    isDirectOrMeek () {
      return this.isMeekijken || this.getProduct === 'direct'
    },
    isMeetingLinkSession: (state) => {
      return state.meeting_link_session === true
    },
    getPlannenSite: (state) => {
      return state.plannenSite
    },
    getPlannenSelectedAgent: (state) => {
      return state.plannenSelectedAgent
    },
    getPlannenAvailableDate: (state) => {
      return state.plannenAvailableDate
    }
  }
})

export function installMainStorePlugins (pinia) {
  const store = useMainStore(pinia)
  store.$onAction(({ name, after }) => {
    after(() => {
      if (name === 'setStatus' && window.parent && window !== window.parent) {
        new Utils().postMessageToParent('HOST_STATUS_UPDATED', store.status)
      }
    })
  })
}
